import React, { useState } from "react";
import type {
  StepMap,
  StepRenderProps,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.types";
import {
  FooterButton,
  FooterContainer,
  StepTitle,
  StepContent,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.styles";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { posthogClientManager } from "@/manager/posthog-client";
import styled from "@emotion/styled";
import { MDSButtonVariant, MDSTextField, MDSTextFieldVariant } from "@/mds";

interface ReferrerOption {
  id: string;
  title: string;
}

const ReferrerOptions: ReferrerOption[] = [
  { id: "gemini", title: "Gemini" },
  { id: "claude-connector", title: "Claude (MCP / Connectors)" },
  { id: "claude-chat", title: "Claude (Chat)" },
  { id: "chatgpt-mcp", title: "ChatGPT (MCP)" },
  { id: "chatgpt-chat", title: "ChatGPT (Chat)" },
  { id: "google-search", title: "Google" },
  { id: "youtube", title: "YouTube" },
  { id: "instagram", title: "Instagram" },
  { id: "linkedin", title: "LinkedIn" },
  { id: "x-twitter", title: "X" },
  { id: "newsletter", title: "Newsletter" },
  { id: "friend", title: "Friend" },
  { id: "coworker", title: "Coworker" },
  { id: "other", title: "Other" },
];

export const ReferrerStep: React.FC<StepRenderProps> = observer(({ onComplete }) => {
  const { store } = useAppStore();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [otherText, setOtherText] = useState<string>("");

  const handleOptionSelect = (optionId: string) => {
    if (selectedOption === optionId) return;
    setSelectedOption(optionId);
    if (optionId !== "other") {
      setOtherText("");
    }
  };

  const showFinish = store.onboarding.showFinish;

  const handleContinue = async () => {
    if (!selectedOption) return;

    const selectedOptionTitle = ReferrerOptions.find(opt => opt.id === selectedOption)?.title;
    const properties = {
      selectedOption,
      selectedOptionTitle,
      otherText: selectedOption === "other" ? otherText.trim() : undefined,
    };
    trackEvent(TrackedEvent.ReferrerStepComplete, properties);

    const posthogUserProperties = {
      referrer_source: selectedOption,
      referrer_source_label: selectedOptionTitle,
      referrer_other_text: selectedOption === "other" ? otherText.trim() : undefined,
      referrer_completed: true,
      referrer_completed_at: new Date().toISOString(),
    };

    const posthogReferrerOnceProperties = {
      initial_referrer_completed_at: new Date().toISOString(),
    };

    posthogClientManager.setPersonProperties(posthogUserProperties, posthogReferrerOnceProperties);

    onComplete("referrer");
  };

  const isContinueEnabled =
    selectedOption && (selectedOption !== "other" || otherText.trim().length > 0);

  return (
    <WizardContentWrapper>
      <StepTitle>How did you hear about Mem?</StepTitle>

      <StepContent>
        <StyledChipsContainer>
          {ReferrerOptions.map(option => (
            <StyledChip
              key={option.id}
              $isSelected={selectedOption === option.id}
              onClick={() => handleOptionSelect(option.id)}
            >
              {option.title}
            </StyledChip>
          ))}
        </StyledChipsContainer>

        {selectedOption === "other" && (
          <StyledOtherInput>
            <MDSTextField
              variant={MDSTextFieldVariant.Form}
              placeholder="Please specify"
              value={otherText}
              onChange={setOtherText}
              autoFocus
            />
          </StyledOtherInput>
        )}
      </StepContent>

      <FooterContainer>
        <FooterButton
          variant={MDSButtonVariant.Contrast}
          onClick={handleContinue}
          isDisabled={!isContinueEnabled}
        >
          {showFinish ? "Finish" : "Next"}
        </FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
});

const StyledChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing["2"]};
  margin-top: ${({ theme }) => theme.spacing["2"]};
`;

const StyledChip = styled.button<{ $isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing["2"]} ${({ theme }) => theme.spacing["5"]};
  border-radius: 100px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.background.button.toggle.toggled : "transparent"};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  outline: none;

  &:hover {
    background: ${({ theme, $isSelected }) =>
      $isSelected
        ? theme.colors.background.button.toggle.toggledHover
        : theme.colors.background.hoverOverlay.default};
  }
`;

const StyledOtherInput = styled.div`
  [data-rac] {
    height: 54px;
    min-height: unset;
    padding-block: 0;

    input {
      font-weight: ${({ theme }) => theme.fontWeights.regular};
    }
  }
`;

export const ReferrerStepsMap: StepMap = {
  referrer: {
    id: "referrer",
    render: props => <ReferrerStep {...props} />,
  },
} as const;
